import {
	Action,
	type Customer,
	ScheduleSessionAttendanceStatus,
} from "@pilatestime/shared-types";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	AlertCircle,
	AlertTriangle,
	Ban,
	Calendar,
	CheckCircle,
	Clock,
	Eye,
	MapPin,
	Pause,
	Plus,
	RefreshCw,
	User,
	UserPlus,
	Users,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchAttendanceBySessionQueryOptions } from "@/api/scheduleSessionAttendance";
import { fetchScheduleSessionQueryOptions } from "@/api/schedules";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	useBulkCreateAttendance,
	useUpdateAttendanceStatus,
} from "@/hooks/useAttendance";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
	useCancelScheduleSession,
	useReactivateScheduleSession,
	useRescheduleScheduleSession,
} from "@/hooks/useScheduleSessions";
import { AddCustomerModal } from "@/routes/_auth/app/_app/org/$orgId/attendance/-components/AddCustomerModal";
import { formatSessionTime } from "@/utils/attendanceUtils";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/attendance/session/$sessionId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, sessionId } = params;

		// Preload session data
		context.queryClient.ensureQueryData(
			fetchScheduleSessionQueryOptions(orgId, sessionId),
		);

		// Preload attendance data
		context.queryClient.ensureQueryData(
			fetchAttendanceBySessionQueryOptions(token, orgId, sessionId, userId),
		);

		return { userId, token, orgId, sessionId };
	},
	component: SessionDetailsPage,
});

function SessionDetailsPage() {
	const { t } = useTranslation();
	const { formatDateWithOptions } = useDateFormatter();
	const { token, orgId, sessionId, userId } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const checkCan = useCan();
	const canManageAttendance = checkCan(Action.MANAGE_ATTENDANCE);

	// Modal state
	const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
	const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

	// Reschedule form state
	const [rescheduleDate, setRescheduleDate] = useState("");
	const [rescheduleStartTime, setRescheduleStartTime] = useState("");
	const [rescheduleEndTime, setRescheduleEndTime] = useState("");

	// Mutations
	const bulkCreateAttendanceMutation = useBulkCreateAttendance({
		error: t("attendance.addCustomer.errors.failed", { error: "{{error}}" }),
	});

	const updateAttendanceStatusMutation = useUpdateAttendanceStatus({
		success: t("attendance.statusUpdate.success"),
		error: t("attendance.statusUpdate.error"),
	});

	const cancelSessionMutation = useCancelScheduleSession(orgId);
	const reactivateSessionMutation = useReactivateScheduleSession(orgId);
	const rescheduleSessionMutation = useRescheduleScheduleSession(orgId);

	// Handle attendance status change
	const handleStatusChange = (
		customerId: string,
		newStatus: ScheduleSessionAttendanceStatus,
	) => {
		updateAttendanceStatusMutation.mutate({
			token,
			organizationId: orgId,
			sessionId,
			customerId,
			userId,
			status: newStatus,
		});
	};

	// Get preloaded data using useSuspenseQuery
	const { data: session } = useSuspenseQuery(
		fetchScheduleSessionQueryOptions(orgId, sessionId),
	);

	const { data: attendances } = useSuspenseQuery(
		fetchAttendanceBySessionQueryOptions(token, orgId, sessionId, userId),
	);

	// Ensure attendances is always an array
	const safeAttendances = attendances || [];

	// Format session date
	const formatDate = (timestamp: number) => {
		return formatDateWithOptions(timestamp, {
			timeZone: "UTC",
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	// Get attendance status icon and color
	const getAttendanceStatusDisplay = (
		status: ScheduleSessionAttendanceStatus,
	) => {
		switch (status) {
			case ScheduleSessionAttendanceStatus.CONFIRMED:
				return {
					icon: <CheckCircle className="h-4 w-4 text-success" />,
					label: t("attendance.status.confirmed"),
					color: "success",
				};
			case ScheduleSessionAttendanceStatus.CANCELED:
				return {
					icon: <XCircle className="h-4 w-4 text-error" />,
					label: t("attendance.status.canceled"),
					color: "error",
				};
			case ScheduleSessionAttendanceStatus.TRIAL:
				return {
					icon: <AlertCircle className="h-4 w-4 text-warning" />,
					label: t("attendance.status.trial"),
					color: "warning",
				};
			case ScheduleSessionAttendanceStatus.PAUSED:
				return {
					icon: <AlertCircle className="h-4 w-4 text-warning" />,
					label: t("attendance.status.paused"),
					color: "warning",
				};
			case ScheduleSessionAttendanceStatus.REPOSITION:
				return {
					icon: <AlertCircle className="h-4 w-4 text-info" />,
					label: t("attendance.status.reposition"),
					color: "info",
				};
			default:
				return {
					icon: <AlertCircle className="h-4 w-4 text-base-content/50" />,
					label: status,
					color: "neutral",
				};
		}
	};

	// Handle adding customers to session
	const handleAddCustomers = async (
		customersWithStatus: {
			customer: Customer;
			status: ScheduleSessionAttendanceStatus;
		}[],
	) => {
		if (customersWithStatus.length === 0) {
			toast.error(t("attendance.addCustomer.errors.noCustomersSelected"));
			return;
		}

		// Transform the data to match the API format
		const customersForAPI = customersWithStatus.map((item) => ({
			customerId: item.customer.id,
			status: item.status,
		}));

		// Get customer names for success message
		const customerNames = customersWithStatus
			.map((item) => `${item.customer.firstName} ${item.customer.lastName}`)
			.join(", ");

		// Call the bulk create attendance mutation
		bulkCreateAttendanceMutation.mutate(
			{
				token,
				organizationId: orgId,
				sessionId,
				userId,
				customersWithStatus: customersForAPI,
			},
			{
				onSuccess: () => {
					// Show custom success message with customer names
					toast.success(
						t("attendance.addCustomer.success", {
							count: customersWithStatus.length,
							customers: customerNames,
						}),
					);
					// Close the modal
					setIsAddCustomerModalOpen(false);
				},
			},
		);
	};

	// Calculate capacity info
	const getCapacityStatus = () => {
		if (!session.capacity) {
			return { status: "unlimited", percentage: 0 };
		}
		const ratio = session.enrolledCustomers / session.capacity;
		const percentage = Math.min(ratio * 100, 100);
		if (session.enrolledCustomers >= session.capacity) {
			return { status: "full", percentage };
		}
		if (ratio >= 0.8) {
			return { status: "almostFull", percentage };
		}
		return { status: "available", percentage };
	};

	const capacityStatus = getCapacityStatus();

	const handleCancelSession = () => {
		cancelSessionMutation.mutate(sessionId, {
			onSuccess: () => {
				toast.success(t("attendance.sessionDetails.cancelSession.success"));
				setIsCancelModalOpen(false);
				// Invalidate and refetch session data
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions"],
				});
				// Navigate back to attendance page
				navigate({ to: "/app/org/$orgId/attendance", params: { orgId } });
			},
			onError: () => {
				toast.error(t("attendance.sessionDetails.cancelSession.error"));
			},
		});
	};

	const handleReactivateSession = () => {
		reactivateSessionMutation.mutate(sessionId, {
			onSuccess: () => {
				toast.success(t("attendance.sessionDetails.reactivateSession.success"));
				setIsReactivateModalOpen(false);
				// Invalidate and refetch session data
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions"],
				});
				// Also invalidate the session detail query to refresh the page
				queryClient.invalidateQueries({
					queryKey: ["scheduleSession", orgId, sessionId],
				});
			},
			onError: () => {
				toast.error(t("attendance.sessionDetails.reactivateSession.error"));
			},
		});
	};

	const handleOpenRescheduleModal = () => {
		// Pre-populate with current session date
		const sessionDate = new Date(session.sessionDate);
		const year = sessionDate.getUTCFullYear();
		const month = String(sessionDate.getUTCMonth() + 1).padStart(2, "0");
		const day = String(sessionDate.getUTCDate()).padStart(2, "0");
		setRescheduleDate(`${year}-${month}-${day}`);

		// Pre-populate with current session times
		setRescheduleStartTime(session.startTime);
		setRescheduleEndTime(session.endTime);

		setIsCancelModalOpen(false);
		setIsRescheduleModalOpen(true);
	};

	const handleRescheduleSession = () => {
		if (!rescheduleDate) {
			toast.error(t("attendance.sessionDetails.rescheduleSession.validation"));
			return;
		}

		if (!rescheduleStartTime || !rescheduleEndTime) {
			toast.error(
				t("attendance.sessionDetails.rescheduleSession.timeValidation"),
			);
			return;
		}

		// Validate end time is after start time
		if (rescheduleStartTime >= rescheduleEndTime) {
			toast.error(
				t("attendance.sessionDetails.rescheduleSession.timeOrderValidation"),
			);
			return;
		}

		// Parse the date and convert to epoch timestamp (UTC midnight)
		const [year, month, day] = rescheduleDate.split("-").map(Number);
		const newSessionDate = Date.UTC(year, month - 1, day);

		rescheduleSessionMutation.mutate(
			{
				sessionId,
				newSessionDate,
				newStartTime: rescheduleStartTime,
				newEndTime: rescheduleEndTime,
			},
			{
				onSuccess: (result) => {
					toast.success(
						t("attendance.sessionDetails.rescheduleSession.success"),
					);
					setIsRescheduleModalOpen(false);
					// Invalidate and refetch session data
					queryClient.invalidateQueries({
						queryKey: ["scheduleSessions"],
					});
					// Navigate to the new session page
					navigate({
						to: "/app/org/$orgId/attendance/session/$sessionId",
						params: { orgId, sessionId: result.newSession.id },
					});
				},
				onError: () => {
					toast.error(t("attendance.sessionDetails.rescheduleSession.error"));
				},
			},
		);
	};

	return (
		<div className="bg-base-200 pb-8">
			{/* Header */}
			<div className="bg-base-100 border-b border-base-300 sticky top-0 z-10">
				<div className="container mx-auto px-4 py-3 md:py-4">
					<PageHeader
						title={session.name}
						description={t("attendance.sessionDetails.title")}
						showBackButton
						backButtonText={t("common.back")}
						actions={
							canManageAttendance ? (
								<div className="flex items-center gap-2">
									<Button variant="outline" size="sm" asChild>
										<Link
											to="/app/org/$orgId/schedules/$scheduleId"
											params={{ orgId, scheduleId: session.scheduleId }}
											className="flex items-center gap-2"
										>
											<Eye className="h-4 w-4" />
											<span className="hidden md:inline">
												{t("attendance.sessionDetails.viewSchedule")}
											</span>
										</Link>
									</Button>
									{session.isCanceled ? (
										<Button
											variant="outline"
											size="sm"
											onClick={() => setIsReactivateModalOpen(true)}
											className="text-success border-success hover:bg-success/10"
										>
											<RefreshCw className="h-4 w-4 md:mr-2" />
											<span className="hidden md:inline">
												{t(
													"attendance.sessionDetails.reactivateSession.button",
												)}
											</span>
										</Button>
									) : (
										<Button
											variant="outline"
											size="sm"
											onClick={() => setIsCancelModalOpen(true)}
											className="text-error border-error hover:bg-error/10"
										>
											<Ban className="h-4 w-4 md:mr-2" />
											<span className="hidden md:inline">
												{t("attendance.sessionDetails.cancelSession.button")}
											</span>
										</Button>
									)}
								</div>
							) : undefined
						}
					/>
				</div>
			</div>

			{/* Session Canceled Banner */}
			{session.isCanceled && (
				<div className="container mx-auto px-4 pt-4">
					<div className="bg-error/10 border border-error/20 rounded-lg p-3 md:p-4 flex items-center gap-2 md:gap-3">
						<AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-error flex-shrink-0" />
						<p className="text-error font-medium text-sm md:text-base">
							{t("attendance.sessionDetails.sessionCanceled")}
						</p>
					</div>
				</div>
			)}

			{/* Cancel Session Confirmation Dialog */}
			<Dialog open={isCancelModalOpen} onOpenChange={setIsCancelModalOpen}>
				<DialogContent className="max-w-[90vw] sm:max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-base md:text-lg">
							<AlertTriangle className="h-5 w-5 text-warning flex-shrink-0" />
							{t("attendance.sessionDetails.cancelSession.title")}
						</DialogTitle>
						<DialogDescription className="text-sm md:text-base">
							{t("attendance.sessionDetails.cancelSession.description")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-2">
						<Button
							variant="outline"
							onClick={() => setIsCancelModalOpen(false)}
							disabled={cancelSessionMutation.isPending}
							className="w-full sm:w-auto"
						>
							{t("common.cancel")}
						</Button>
						<Button
							variant="secondary"
							onClick={handleOpenRescheduleModal}
							disabled={cancelSessionMutation.isPending}
							className="w-full sm:w-auto"
						>
							<Calendar className="h-4 w-4 mr-2" />
							{t("attendance.sessionDetails.rescheduleSession.button")}
						</Button>
						<Button
							variant="destructive"
							onClick={handleCancelSession}
							disabled={cancelSessionMutation.isPending}
							className="bg-error hover:bg-error/90 text-error-content w-full sm:w-auto"
						>
							{cancelSessionMutation.isPending
								? t("attendance.sessionDetails.cancelSession.canceling")
								: t("attendance.sessionDetails.cancelSession.confirm")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reactivate Session Confirmation Dialog */}
			<Dialog
				open={isReactivateModalOpen}
				onOpenChange={setIsReactivateModalOpen}
			>
				<DialogContent className="max-w-[90vw] sm:max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-base md:text-lg">
							<RefreshCw className="h-5 w-5 text-success flex-shrink-0" />
							{t("attendance.sessionDetails.reactivateSession.title")}
						</DialogTitle>
						<DialogDescription className="text-sm md:text-base">
							{t("attendance.sessionDetails.reactivateSession.description")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-2">
						<Button
							variant="outline"
							onClick={() => setIsReactivateModalOpen(false)}
							disabled={reactivateSessionMutation.isPending}
							className="w-full sm:w-auto"
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleReactivateSession}
							disabled={reactivateSessionMutation.isPending}
							className="bg-success hover:bg-success/90 text-success-content w-full sm:w-auto"
						>
							{reactivateSessionMutation.isPending
								? t("attendance.sessionDetails.reactivateSession.reactivating")
								: t("attendance.sessionDetails.reactivateSession.confirm")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Reschedule Session Dialog */}
			<Dialog
				open={isRescheduleModalOpen}
				onOpenChange={setIsRescheduleModalOpen}
			>
				<DialogContent className="max-w-[90vw] sm:max-w-lg">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-base md:text-lg">
							<Calendar className="h-5 w-5 text-primary flex-shrink-0" />
							{t("attendance.sessionDetails.rescheduleSession.title")}
						</DialogTitle>
						<DialogDescription className="text-sm md:text-base">
							{t("attendance.sessionDetails.rescheduleSession.description")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="reschedule-date">
								{t("attendance.sessionDetails.rescheduleSession.newDate")}
							</Label>
							<Input
								id="reschedule-date"
								type="date"
								value={rescheduleDate}
								onChange={(e) => setRescheduleDate(e.target.value)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="reschedule-start-time">
									{t(
										"attendance.sessionDetails.rescheduleSession.newStartTime",
									)}
								</Label>
								<div className="flex items-center gap-2">
									<Clock className="h-4 w-4 text-base-content/50" />
									<Input
										id="reschedule-start-time"
										type="time"
										value={rescheduleStartTime}
										onChange={(e) => setRescheduleStartTime(e.target.value)}
										className="flex-1"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label htmlFor="reschedule-end-time">
									{t("attendance.sessionDetails.rescheduleSession.newEndTime")}
								</Label>
								<div className="flex items-center gap-2">
									<Clock className="h-4 w-4 text-base-content/50" />
									<Input
										id="reschedule-end-time"
										type="time"
										value={rescheduleEndTime}
										onChange={(e) => setRescheduleEndTime(e.target.value)}
										className="flex-1"
									/>
								</div>
							</div>
						</div>
					</div>
					<DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-0">
						<Button
							variant="outline"
							onClick={() => setIsRescheduleModalOpen(false)}
							disabled={rescheduleSessionMutation.isPending}
							className="w-full sm:w-auto"
						>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleRescheduleSession}
							disabled={rescheduleSessionMutation.isPending}
							className="bg-primary hover:bg-primary/90 text-primary-content w-full sm:w-auto"
						>
							{rescheduleSessionMutation.isPending
								? t("attendance.sessionDetails.rescheduleSession.rescheduling")
								: t("attendance.sessionDetails.rescheduleSession.confirm")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Main Content */}
			<div className="container mx-auto px-4 py-4 md:py-6">
				{/* Session Info Hero Card - Mobile Optimized */}
				<Card className="mb-4 md:mb-6 overflow-hidden">
					<div
						className="h-1.5 md:h-2"
						style={{ backgroundColor: session.staffMemberColor }}
						aria-hidden="true"
					/>
					<CardContent className="p-3 md:p-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
							{/* Date & Time */}
							<div className="flex items-start gap-2 md:gap-3">
								<div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center">
									<Calendar className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-[10px] md:text-xs text-base-content/60 uppercase tracking-wide font-medium">
										{t("attendance.sessionDetails.details")}
									</p>
									<p className="font-semibold text-sm md:text-base text-base-content break-words">
										{formatDate(new Date(session.sessionDate).getTime())}
									</p>
									<p className="text-xs md:text-sm text-base-content/70 flex items-center gap-1">
										<Clock className="h-3 w-3 md:h-3.5 md:w-3.5" />
										{formatSessionTime(session.startTime, session.endTime)}
									</p>
								</div>
							</div>

							{/* Instructor */}
							<div className="flex items-start gap-2 md:gap-3">
								<div
									className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-xs md:text-sm"
									style={{ backgroundColor: session.staffMemberColor }}
								>
									{session.staffMemberName?.charAt(0).toUpperCase() || "?"}
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-[10px] md:text-xs text-base-content/60 uppercase tracking-wide font-medium">
										{t("attendance.sessionDetails.instructor")}
									</p>
									<p className="font-semibold text-sm md:text-base text-base-content break-words">
										{session.staffMemberName}
									</p>
								</div>
							</div>

							{/* Location */}
							<div className="flex items-start gap-2 md:gap-3">
								<div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-secondary/10 flex items-center justify-center">
									<MapPin className="h-4 w-4 md:h-5 md:w-5 text-secondary" />
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-[10px] md:text-xs text-base-content/60 uppercase tracking-wide font-medium">
										{t("attendance.sessionDetails.location")}
									</p>
									<p className="font-semibold text-sm md:text-base text-base-content break-words">
										{session.establishmentName}
									</p>
								</div>
							</div>

							{/* Capacity */}
							<div className="flex items-start gap-2 md:gap-3">
								<div
									className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
										capacityStatus.status === "full"
											? "bg-error/10"
											: capacityStatus.status === "almostFull"
												? "bg-warning/10"
												: "bg-success/10"
									}`}
								>
									<Users
										className={`h-4 w-4 md:h-5 md:w-5 ${
											capacityStatus.status === "full"
												? "text-error"
												: capacityStatus.status === "almostFull"
													? "text-warning"
													: "text-success"
										}`}
									/>
								</div>
								<div className="min-w-0 flex-1">
									<p className="text-[10px] md:text-xs text-base-content/60 uppercase tracking-wide font-medium">
										{t("attendance.sessionDetails.capacity")}
									</p>
									<p className="font-semibold text-sm md:text-base text-base-content">
										{session.enrolledCustomers}
										{session.capacity ? `/${session.capacity}` : ""}
									</p>
									{session.capacity && (
										<div className="mt-1 w-full bg-base-300 rounded-full h-1 md:h-1.5 overflow-hidden">
											<div
												className={`h-full rounded-full transition-all ${
													capacityStatus.status === "full"
														? "bg-error"
														: capacityStatus.status === "almostFull"
															? "bg-warning"
															: "bg-success"
												}`}
												style={{ width: `${capacityStatus.percentage}%` }}
											/>
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Description & Notes */}
						{(session.description || session.notes) && (
							<div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-base-300 space-y-2 md:space-y-3">
								{session.description && (
									<p className="text-xs md:text-sm text-base-content/70">
										{session.description}
									</p>
								)}
								{session.notes && (
									<div className="bg-base-200 rounded-lg p-2 md:p-3">
										<p className="text-[10px] md:text-xs text-base-content/60 uppercase tracking-wide font-medium mb-1">
											{t("attendance.sessionDetails.notes")}
										</p>
										<p className="text-xs md:text-sm text-base-content/80">
											{session.notes}
										</p>
									</div>
								)}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Attendance Section */}
				<Card>
					<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
						<div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
							<CardTitle className="flex items-center gap-2 text-base md:text-lg">
								<Users className="h-4 w-4 md:h-5 md:w-5" />
								{t("attendance.sessionDetails.attendanceList")}
								<span className="ml-1 px-1.5 md:px-2 py-0.5 bg-base-200 rounded-full text-xs md:text-sm font-normal">
									{safeAttendances.length}
								</span>
							</CardTitle>
							{canManageAttendance && (
								<Button
									onClick={() => setIsAddCustomerModalOpen(true)}
									size="sm"
									className="flex items-center gap-2 text-primary-content w-full sm:w-auto text-sm"
								>
									<UserPlus className="h-4 w-4" />
									<span className="hidden xs:inline">
										{t("attendance.addCustomer.buttonLabel")}
									</span>
									<span className="xs:hidden">
										{t("attendance.addCustomer.buttonLabelShort")}
									</span>
								</Button>
							)}
						</div>
					</CardHeader>
					<CardContent className="p-0 sm:p-6 sm:pt-0">
						{safeAttendances.length === 0 ? (
							<div className="text-center py-8 md:py-12 px-4">
								<div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-base-200 flex items-center justify-center">
									<Users className="h-6 w-6 md:h-8 md:w-8 text-base-content/30" />
								</div>
								<p className="text-sm md:text-base text-base-content/70 mb-3 md:mb-4">
									{t("attendance.sessionDetails.noAttendance")}
								</p>
								{canManageAttendance && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setIsAddCustomerModalOpen(true)}
										className="inline-flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										{t("attendance.addCustomer.buttonLabel")}
									</Button>
								)}
							</div>
						) : (
							<div className="divide-y divide-base-200">
								{safeAttendances.map((attendance) => {
									const attendanceStatusDisplay = getAttendanceStatusDisplay(
										attendance.status,
									);
									const initials =
										`${attendance.customer.firstName?.charAt(0) || ""}${attendance.customer.lastName?.charAt(0) || ""}`.toUpperCase();

									return (
										<div
											key={attendance.id}
											className="flex items-center gap-2 md:gap-3 p-3 md:p-4 hover:bg-base-200/50 transition-colors active:bg-base-200/70"
										>
											{/* Avatar */}
											<div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center">
												<span className="text-xs md:text-sm font-semibold text-primary">
													{initials || (
														<User className="h-4 w-4 md:h-5 md:w-5" />
													)}
												</span>
											</div>

											{/* Customer Info */}
											<div className="flex-1 min-w-0">
												<p className="font-medium text-sm md:text-base text-base-content break-words">
													{attendance.customer.firstName}{" "}
													{attendance.customer.lastName}
												</p>
												<p className="text-[10px] md:text-xs text-base-content/60 flex items-center gap-1 sm:hidden">
													{attendanceStatusDisplay.icon}
													<span>{attendanceStatusDisplay.label}</span>
												</p>
											</div>

											{/* Status Selector - Desktop */}
											{canManageAttendance ? (
												<div className="hidden sm:flex items-center gap-2">
													{attendanceStatusDisplay.icon}
													<Select
														value={attendance.status}
														onValueChange={(value) =>
															handleStatusChange(
																attendance.customer.id,
																value as ScheduleSessionAttendanceStatus,
															)
														}
														disabled={updateAttendanceStatusMutation.isPending}
													>
														<SelectTrigger className="w-36 h-9">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem
																value={
																	ScheduleSessionAttendanceStatus.CONFIRMED
																}
															>
																<div className="flex items-center gap-2">
																	<CheckCircle className="h-4 w-4 text-success" />
																	{t("attendance.status.confirmed")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.TRIAL}
															>
																<div className="flex items-center gap-2">
																	<AlertCircle className="h-4 w-4 text-warning" />
																	{t("attendance.status.trial")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.PAUSED}
															>
																<div className="flex items-center gap-2">
																	<Pause className="h-4 w-4 text-warning" />
																	{t("attendance.status.paused")}
																</div>
															</SelectItem>
															<SelectItem
																value={
																	ScheduleSessionAttendanceStatus.REPOSITION
																}
															>
																<div className="flex items-center gap-2">
																	<AlertCircle className="h-4 w-4 text-info" />
																	{t("attendance.status.reposition")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.CANCELED}
															>
																<div className="flex items-center gap-2">
																	<XCircle className="h-4 w-4 text-error" />
																	{t("attendance.status.canceled")}
																</div>
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
											) : (
												<div className="hidden sm:flex items-center gap-2">
													{attendanceStatusDisplay.icon}
													<span className="text-sm">
														{attendanceStatusDisplay.label}
													</span>
												</div>
											)}

											{/* Status Selector - Mobile */}
											{canManageAttendance ? (
												<div className="sm:hidden flex-shrink-0">
													<Select
														value={attendance.status}
														onValueChange={(value) =>
															handleStatusChange(
																attendance.customer.id,
																value as ScheduleSessionAttendanceStatus,
															)
														}
														disabled={updateAttendanceStatusMutation.isPending}
													>
														<SelectTrigger className="w-[100px] h-9 text-xs px-2">
															<SelectValue />
														</SelectTrigger>
														<SelectContent>
															<SelectItem
																value={
																	ScheduleSessionAttendanceStatus.CONFIRMED
																}
																className="py-3"
															>
																<div className="flex items-center gap-2">
																	<CheckCircle className="h-4 w-4 text-success" />
																	{t("attendance.status.confirmed")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.TRIAL}
																className="py-3"
															>
																<div className="flex items-center gap-2">
																	<AlertCircle className="h-4 w-4 text-warning" />
																	{t("attendance.status.trial")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.PAUSED}
																className="py-3"
															>
																<div className="flex items-center gap-2">
																	<AlertCircle className="h-4 w-4 text-warning" />
																	{t("attendance.status.paused")}
																</div>
															</SelectItem>
															<SelectItem
																value={
																	ScheduleSessionAttendanceStatus.REPOSITION
																}
																className="py-3"
															>
																<div className="flex items-center gap-2">
																	<AlertCircle className="h-4 w-4 text-info" />
																	{t("attendance.status.reposition")}
																</div>
															</SelectItem>
															<SelectItem
																value={ScheduleSessionAttendanceStatus.CANCELED}
																className="py-3"
															>
																<div className="flex items-center gap-2">
																	<XCircle className="h-4 w-4 text-error" />
																	{t("attendance.status.canceled")}
																</div>
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Add Customer Modal */}
			<AddCustomerModal
				isOpen={isAddCustomerModalOpen}
				onClose={() => setIsAddCustomerModalOpen(false)}
				onAddCustomers={handleAddCustomers}
				organizationId={orgId}
				token={token}
				userId={userId}
			/>
		</div>
	);
}
